// Standard MIDI File (Type 1) writer and a small parser used for round-trip tests.

import type { Interpretation } from './types';

export interface MidiEvent {
  /** Absolute time in MIDI ticks. */
  tick: number;
  /** Raw bytes without delta time (status byte first, or FF type len data for meta events). */
  data: number[];
  /** Sort priority for events at the same tick (lower first). */
  order: number;
}

export interface MidiTrackData {
  events: MidiEvent[];
}

function vlq(value: number): number[] {
  let v = Math.max(0, Math.floor(value));
  const bytes = [v & 0x7f];
  while ((v >>= 7) > 0) bytes.unshift((v & 0x7f) | 0x80);
  return bytes;
}

function text(type: number, s: string): number[] {
  const bytes = Array.from(s, (c) => {
    const code = c.charCodeAt(0);
    return code < 256 ? code : 0x3f;
  });
  return [0xff, type, ...vlq(bytes.length), ...bytes];
}

export function encodeSmf(tracks: MidiTrackData[], ppq: number): Uint8Array {
  const chunks: number[][] = [];
  for (const tr of tracks) {
    const evs = tr.events.slice().sort((a, b) => a.tick - b.tick || a.order - b.order);
    const body: number[] = [];
    let last = 0;
    for (const e of evs) {
      const tick = Math.max(last, Math.round(e.tick));
      body.push(...vlq(tick - last), ...e.data);
      last = tick;
    }
    body.push(0x00, 0xff, 0x2f, 0x00);
    chunks.push(body);
  }
  const size = 14 + chunks.reduce((n, c) => n + 8 + c.length, 0);
  const out = new Uint8Array(size);
  let o = 0;
  const w = (...b: number[]) => {
    for (const x of b) out[o++] = x;
  };
  const w32 = (v: number) => w((v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff);
  w(0x4d, 0x54, 0x68, 0x64);
  w32(6);
  w(0, 1, (tracks.length >> 8) & 0xff, tracks.length & 0xff, (ppq >> 8) & 0xff, ppq & 0xff);
  for (const c of chunks) {
    w(0x4d, 0x54, 0x72, 0x6b);
    w32(c.length);
    out.set(c, o);
    o += c.length;
  }
  return out;
}

export interface SongMeta {
  title: string;
  author: string;
  released: string;
}

export interface MidiBuildOptions {
  ppq: number;
  /** Only include these track indices (plus the conductor track). */
  onlyTracks?: number[];
}

/** Builds a Type-1 file: conductor track 0, then one track per interpreted track. */
export function buildMidi(it: Interpretation, meta: SongMeta, opts: MidiBuildOptions): Uint8Array {
  const ppq = Math.max(24, Math.min(9600, Math.round(opts.ppq)));
  const ticksPerSec = (it.bpm / 60) * ppq;
  const toTick = (sec: number) => Math.round(sec * ticksPerSec);
  const mpq = Math.round(60_000_000 / it.bpm);

  const conductor: MidiEvent[] = [
    { tick: 0, order: 0, data: text(0x03, meta.title || 'SID tune') },
    { tick: 0, order: 0, data: [0xff, 0x51, 0x03, (mpq >> 16) & 0xff, (mpq >> 8) & 0xff, mpq & 0xff] },
    { tick: 0, order: 0, data: [0xff, 0x58, 0x04, 0x04, 0x02, 0x18, 0x08] },
  ];
  if (meta.author) conductor.push({ tick: 0, order: 1, data: text(0x01, `Composer: ${meta.author}`) });
  if (meta.released) conductor.push({ tick: 0, order: 1, data: text(0x02, meta.released) });
  conductor.push({ tick: 0, order: 1, data: text(0x01, 'Converted with SID2MIDI Web') });

  const include = opts.onlyTracks ? new Set(opts.onlyTracks) : null;
  const tracks: MidiTrackData[] = [{ events: conductor }];

  it.tracks.forEach((info, ti) => {
    if (include && !include.has(ti)) return;
    const events: MidiEvent[] = [{ tick: 0, order: 0, data: text(0x03, info.name) }];
    const channels = new Set<number>();
    for (const n of it.notes) if (n.track === ti) channels.add(n.channel);
    for (const c of it.ccs) if (c.track === ti) channels.add(c.channel);
    if (!channels.size) channels.add(info.channel);
    // Pitch-bend range via RPN 0 so synths follow the bend data correctly.
    for (const ch of channels) {
      if (ch === 9) continue;
      const cc = (num: number, val: number) => ({ tick: 0, order: 1, data: [0xb0 | ch, num, val] });
      events.push(
        cc(101, 0),
        cc(100, 0),
        cc(6, Math.min(24, it.bendRange)),
        cc(38, 0),
        cc(101, 127),
        cc(100, 127),
      );
    }
    for (const n of it.notes) {
      if (n.track !== ti) continue;
      const on = toTick(n.start);
      const off = Math.max(on + 1, toTick(n.end));
      events.push({
        tick: on,
        order: 4,
        data: [0x90 | n.channel, n.note & 0x7f, Math.max(1, n.velocity & 0x7f)],
      });
      events.push({ tick: off, order: 2, data: [0x80 | n.channel, n.note & 0x7f, 0x40] });
    }
    for (const b of it.bends) {
      if (b.track !== ti) continue;
      const v = Math.max(0, Math.min(16383, Math.round(8192 + b.value * 8191)));
      events.push({ tick: toTick(b.time), order: 3, data: [0xe0 | b.channel, v & 0x7f, v >> 7] });
    }
    for (const c of it.ccs) {
      if (c.track !== ti) continue;
      events.push({
        tick: toTick(c.time),
        order: 3,
        data: [0xb0 | c.channel, c.controller & 0x7f, c.value & 0x7f],
      });
    }
    tracks.push({ events });
  });
  return encodeSmf(tracks, ppq);
}

/** Safe file name: "<title> - <author> - subtune <n>.mid". */
export function midiFileName(meta: SongMeta, subtune: number, suffix = ''): string {
  const clean = (s: string) =>
    Array.from(s)
      .filter((c) => c.charCodeAt(0) >= 0x20 && !'\\/:*?"<>|'.includes(c))
      .join('')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 60);
  const parts = [clean(meta.title) || 'SID tune', clean(meta.author)].filter(Boolean);
  return `${parts.join(' - ')} - subtune ${subtune}${suffix}.mid`;
}

// ---------- parser ----------

export interface ParsedEvent {
  tick: number;
  type: 'noteOn' | 'noteOff' | 'cc' | 'bend' | 'program' | 'meta' | 'other';
  channel?: number;
  a?: number;
  b?: number;
  metaType?: number;
  metaData?: number[];
}

export interface ParsedMidi {
  format: number;
  ppq: number;
  tracks: ParsedEvent[][];
}

export function parseSmf(bytes: Uint8Array): ParsedMidi {
  let o = 0;
  const r8 = () => {
    if (o >= bytes.length) throw new Error('Unexpected end of MIDI data');
    return bytes[o++];
  };
  const r16 = () => (r8() << 8) | r8();
  const r32 = () => ((r8() << 24) | (r8() << 16) | (r8() << 8) | r8()) >>> 0;
  const rvlq = () => {
    let v = 0;
    for (let i = 0; i < 4; i++) {
      const b = r8();
      v = (v << 7) | (b & 0x7f);
      if (!(b & 0x80)) return v;
    }
    throw new Error('Invalid variable-length quantity');
  };
  const tag = () => String.fromCharCode(r8(), r8(), r8(), r8());
  if (tag() !== 'MThd') throw new Error('Missing MThd');
  if (r32() !== 6) throw new Error('Bad header length');
  const format = r16();
  const ntracks = r16();
  const ppq = r16();
  const tracks: ParsedEvent[][] = [];
  for (let t = 0; t < ntracks; t++) {
    if (tag() !== 'MTrk') throw new Error('Missing MTrk');
    const len = r32();
    const end = o + len;
    const evs: ParsedEvent[] = [];
    let tick = 0;
    let status = 0;
    let ended = false;
    while (o < end) {
      tick += rvlq();
      let b = r8();
      if (b === 0xff) {
        const metaType = r8();
        const l = rvlq();
        const metaData = Array.from(bytes.slice(o, o + l));
        o += l;
        evs.push({ tick, type: 'meta', metaType, metaData });
        if (metaType === 0x2f) ended = true;
        continue;
      }
      if (b === 0xf0 || b === 0xf7) {
        o += rvlq();
        evs.push({ tick, type: 'other' });
        continue;
      }
      if (b & 0x80) status = b;
      else o--;
      b = status;
      const hi = b & 0xf0;
      const channel = b & 0x0f;
      const a = r8();
      const two = hi !== 0xc0 && hi !== 0xd0;
      const c = two ? r8() : 0;
      if (hi === 0x90 && c > 0) evs.push({ tick, type: 'noteOn', channel, a, b: c });
      else if (hi === 0x80 || hi === 0x90) evs.push({ tick, type: 'noteOff', channel, a, b: c });
      else if (hi === 0xb0) evs.push({ tick, type: 'cc', channel, a, b: c });
      else if (hi === 0xe0) evs.push({ tick, type: 'bend', channel, a: a | (c << 7) });
      else if (hi === 0xc0) evs.push({ tick, type: 'program', channel, a });
      else evs.push({ tick, type: 'other', channel, a, b: c });
    }
    if (!ended) throw new Error('Track without end-of-track event');
    tracks.push(evs);
  }
  return { format, ppq, tracks };
}
